import React, { memo, useMemo, useState } from 'react';
import { useBloc } from '@core/utils/bloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { GCCalendarEventObjectType, GCCalendarEventResponse } from '@business/entities/gc_calendar';
import { OrgId } from '@business/entities/organization';
import { CommonService } from '@business/services';
import { GCCalendarEventsListBloc } from './GCCalendarEventsListBloc';
import { Card, CardBody, Col, Row, Badge, CardTitle, Collapse } from 'reactstrap';
import { toMoment } from '@core/utils/time';
import { EventPopper } from '@modules/GCCalendarPage/EventPopper';
import { usePopperAnchor } from '@core/components/Popper';
import { TenantUserResponse, UserResponse } from '@business/entities/account';
import { R } from '@core/utils/r';
import moment from 'moment';
import './index.scss';

export interface GCCalendarEventsListProps {
  objectId?: string;
  objectType?: GCCalendarEventObjectType;
  orgId?: OrgId;
}

export const GCCalendarEventsList = memo((props: GCCalendarEventsListProps) => {
  const { objectId, objectType, orgId } = props;
  const bloc = useBloc(GCCalendarEventsListBloc, objectType, objectId);
  const events = useObservable(bloc.events$, []);
  const users = useObservable(bloc.users$, []);
  const [autoRefresh, setAutoRefresh] = useState(0);
  const [isOpen, setOpen] = useState(true);

  const orderedEvents = useMemo(() => R.orderBy(events, ['startTime'], ['asc']), [events]);

  if (!orderedEvents || (orderedEvents && !orderedEvents.length)) return null;

  return (
    <div className="gc-calendar-events-list">
      <Card>
        <CardBody>
          <CardTitle className="card-title_items mb-3">
            <div>Calendar Events</div>
            <div className="action">
              <a className="ml-2" onClick={() => setOpen(!isOpen)}>
                <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
              </a>
            </div>
          </CardTitle>
          <Collapse isOpen={isOpen}>
            <Row className="events-row">
              {orderedEvents.map(event => (
                <CalendarEvent key={event.id} event={event} users={users} onDelete={id => bloc.deleteEvent(id).subscribe()} />
              ))}
            </Row>
          </Collapse>
        </CardBody>
      </Card>
    </div>
  );
});

export interface CalendarEventProps {
  event: GCCalendarEventResponse;
  users: (UserResponse | TenantUserResponse)[];
  onDelete: (id: string) => void;
}

export const CalendarEvent = memo((props: CalendarEventProps) => {
  const { event, users, onDelete } = props;
  const eventAnchor = usePopperAnchor();
  const [showPopper, setShowPopper] = useState(false);

  const startTime = useMemo(() => toMoment(event.startTime), [event]);
  const endTime = useMemo(() => toMoment(event.endTime), [event]);
  const isSameDay = useMemo(() => startTime.isSame(endTime, 'day'), [startTime, endTime]);

  const eventStatus = useMemo(() => {
    const now = moment();
    const isToday = startTime.isSame(now, 'day');
    const isPast = endTime.isBefore(now);

    if (isToday) return 'today';
    if (isPast) return 'past';
    return 'upcoming';
  }, [startTime, endTime]);

  const participantUsers = useMemo(() => {
    if (!event.participants || !users) return [];
    return event.participants.filter(p => p.userId);
  }, [event.participants, users]);

  const timeDisplay = useMemo(() => {
    if (isSameDay) {
      return startTime.format('MMM D, YYYY · h:mm A');
    }
    return `${startTime.format('MMM D')} - ${endTime.format('MMM D, YYYY')}`;
  }, [isSameDay, startTime, endTime]);

  //   const descriptionPreview = useMemo(() => {
  //     if (!event.description) return '';
  //     const plainText = HTMLService.strip(event.description);
  //     return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText;
  //   }, [event.description]);

  return (
    <>
      <Col lg={4} md={6} sm={12}>
        <div className={`event-card-wrapper mb-3 event-${eventStatus}`}>
          <div className="d-flex">
            <div className="calendar-icon">
              <i className="bx bx-calendar font-size-22" />
            </div>

            <div className="event-content ml-3">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="mb-0 text-dark font-weight-semibold event-title mr-2">{event.name}</h6>
                <div className="d-flex align-items-center flex-shrink-0">
                  <Badge
                    color={eventStatus === 'today' ? 'success' : eventStatus === 'past' ? 'secondary' : 'info'}
                    className="event-status-badge mr-2"
                  >
                    {eventStatus}
                  </Badge>
                  <div
                    className="view-icon-button"
                    onClick={e => {
                      e.stopPropagation();
                      setShowPopper(true);
                      eventAnchor.onClick(e as any);
                    }}
                  >
                    <i className="bx bx-show font-size-16 text-primary"></i>
                  </div>
                  {/* <IconButton size="small" className="delete-icon-button" onClick={e => {}}>
                    <i className="bx bx-trash font-size-16 mr-2"></i>
                  </IconButton> */}
                </div>
              </div>

              <div className="d-flex align-items-center mb-1">
                <i className="bx bx-time-five text-muted mr-2 event-meta-icon"></i>
                <span className="text-muted event-time">{timeDisplay}</span>
              </div>

              {/* {event.location && (
                <div className="d-flex align-items-center mb-1">
                  <i className="bx bx-map text-muted mr-2 event-meta-icon"></i>
                  <span className="text-muted text-truncate event-location">{event.location}</span>
                </div>
              )} */}

              {/* {descriptionPreview && <p className="text-muted event-description">{descriptionPreview}</p>} */}

              {participantUsers.length > 0 && (
                <div className="d-flex align-items-center flex-wrap mt-2 participants-list">
                  {participantUsers.map((participantUser, index) => (
                    <Badge key={participantUser.id || index} color="light" className="participant-name-badge">
                      {CommonService.getFullName(participantUser.user)}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Col>

      {showPopper && <EventPopper anchor={eventAnchor} event={event} onDelete={onDelete} onEdit={undefined} isMyEvent={true} />}
    </>
  );
});
